/** Cooperative main-thread work: yield between bounded pieces, never alter seeded order. */
export class LoadingTask {
  public readonly timings: Record<string, number> = {};
  private checkpoint = performance.now();

  public constructor(
    public readonly signal: AbortSignal,
    private readonly onProgress: (label: string, progress: number) => void,
  ) {}

  public check(): void {
    this.signal.throwIfAborted();
  }

  public async stage(label: string, progress: number): Promise<void> {
    this.check();
    this.onProgress(label, progress);
    await this.yield();
  }

  public async run<T>(name: string, work: () => T | Promise<T>): Promise<T> {
    this.check();
    const start = performance.now();
    const result = await work();
    this.timings[name] = Math.round(performance.now() - start);
    this.check();
    return result;
  }

  public async drain(steps: Iterable<unknown>): Promise<void> {
    for (const step of steps) {
      void step;
      this.check();
      if (performance.now() - this.checkpoint >= 8) await this.yield();
    }
  }

  public waitFor<T>(work: Promise<T>, timeoutMs = 20000): Promise<T> {
    this.check();
    return new Promise<T>((resolve, reject) => {
      const abort = (): void => finish(() => reject(this.signal.reason));
      const timer = setTimeout(
        () => finish(() => reject(new Error("Scene preparation timed out"))),
        timeoutMs,
      );
      const finish = (complete: () => void): void => {
        clearTimeout(timer);
        this.signal.removeEventListener("abort", abort);
        complete();
      };
      this.signal.addEventListener("abort", abort, { once: true });
      work.then(
        (value) => finish(() => resolve(value)),
        (error) => finish(() => reject(error)),
      );
    });
  }

  private async yield(): Promise<void> {
    // A timer alone need not present a frame. rAF + timer lets progress paint first.
    await new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    this.check();
    this.checkpoint = performance.now();
  }
}
